import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { MessageSquarePlus, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const statusColors = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  applied: 'bg-green-100 text-green-800 border-green-200',
  dismissed: 'bg-gray-100 text-gray-700 border-gray-200'
};

const ResidentChangeRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [notes, setNotes] = useState({});

  const canResolve = user?.role === 'admin' || user?.role === 'case_worker';

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status_filter: filter };
      const res = await axios.get(`${API}/resident-change-requests`, { params });
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to load change requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const resolve = async (id, action) => {
    try {
      await axios.put(`${API}/resident-change-requests/${id}/resolve`, {
        action,
        resolution_notes: notes[id] || null
      });
      toast.success(`Request ${action === 'applied' ? 'applied' : 'dismissed'}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve request');
    }
  };

  return (
    <div className="space-y-6" data-testid="change-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquarePlus className="h-6 w-6 text-amber-600" />
            Resident Change Requests
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            After-hours changes logged by general staff for case worker review and follow-up.
          </p>
        </div>
        <div className="flex gap-2">
          {['pending', 'applied', 'dismissed', 'all'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}-btn`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-gray-500" data-testid="no-requests-msg">
            No {filter !== 'all' ? filter : ''} change requests.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <Card key={req.id} data-testid={`change-request-${req.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">{req.resident_name}</CardTitle>
                    <CardDescription className="flex items-center gap-3 mt-1 text-xs">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" /> {req.requested_by} ({req.requested_by_role})</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(req.created_at).toLocaleString()}</span>
                    </CardDescription>
                  </div>
                  <Badge className={statusColors[req.status]}>{req.status.toUpperCase()}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-800 whitespace-pre-wrap">
                  {req.change_description}
                </div>
                {req.status !== 'pending' && (
                  <div className="text-xs text-gray-500">
                    Resolved by {req.resolved_by} on {req.resolved_at ? new Date(req.resolved_at).toLocaleString() : ''}
                    {req.resolution_notes ? ` — ${req.resolution_notes}` : ''}
                  </div>
                )}
                {canResolve && req.status === 'pending' && (
                  <div className="space-y-2 pt-1">
                    <Textarea
                      placeholder="Resolution note (optional)"
                      rows={2}
                      value={notes[req.id] || ''}
                      onChange={(e) => setNotes({ ...notes, [req.id]: e.target.value })}
                      data-testid={`resolution-note-${req.id}`}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => resolve(req.id, 'applied')}
                        data-testid={`apply-btn-${req.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" /> Mark Applied
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resolve(req.id, 'dismissed')}
                        data-testid={`dismiss-btn-${req.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Dismiss
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResidentChangeRequests;
